import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { AtSign, Flag, Radar } from "lucide-react";
import { useState } from "react";
import { ClientFilter, ClientTag } from "@/components/client-filter";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { queries } from "@/lib/queries";
import { formatDate, formatRelative } from "@/lib/utils";

export const Route = createFileRoute("/app/$workspaceId/paginas-e-pixels")({
  component: PaginasPixelsPage,
});

const TASK_LABELS: Record<string, string> = {
  MANAGE: "Gerenciar",
  CREATE_CONTENT: "Criar conteúdo",
  MODERATE: "Moderar",
  ADVERTISE: "Anunciar",
  ANALYZE: "Analisar",
  MESSAGING: "Mensagens",
};

function PaginasPixelsPage() {
  const { workspaceId } = Route.useParams();
  const [clientId, setClientId] = useState<string | undefined>(undefined);
  const pages = useQuery(queries.pages(workspaceId, clientId));
  const pixels = useQuery(queries.pixels(workspaceId, clientId));
  const clients = useQuery(queries.clients(workspaceId));

  const irParaIntegracoes = (
    <Button asChild variant="outline">
      <Link to="/app/$workspaceId/integracoes" params={{ workspaceId }}>
        Ir para Integrações
      </Link>
    </Button>
  );

  return (
    <>
      <PageHeader
        title="Páginas e pixels"
        description="Páginas do Facebook (com o Instagram vinculado) e pixels das contas conectadas."
        actions={<ClientFilter workspaceId={workspaceId} value={clientId} onChange={setClientId} />}
      />
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Páginas</CardTitle>
            <CardDescription>Páginas que os perfis conectados administram.</CardDescription>
          </CardHeader>
          <CardContent>
            {pages.isPending ? (
              <LoadingState rows={3} />
            ) : pages.isError ? (
              <ErrorState error={pages.error} onRetry={() => void pages.refetch()} />
            ) : pages.data.length === 0 ? (
              <EmptyState
                icon={<Flag className="size-8" aria-hidden />}
                title="Nenhuma página"
                description="Conecte uma conta Meta que administre páginas."
                action={irParaIntegracoes}
              />
            ) : (
              <ul className="divide-y">
                {pages.data.map((p) => (
                  <li key={p.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">
                        {p.link ? (
                          <a
                            href={p.link}
                            target="_blank"
                            rel="noreferrer"
                            className="hover:underline"
                          >
                            {p.name}
                          </a>
                        ) : (
                          p.name
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {p.category ?? "Sem categoria"} · id {p.metaId} · {p.connectionName}
                        {p.clientId ? (
                          <>
                            {" · "}
                            <ClientTag clientId={p.clientId} clients={clients.data} />
                          </>
                        ) : null}
                      </p>
                      {p.tasks.length > 0 ? (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {p.tasks.map((t) => (
                            <Badge key={t} variant="outline">
                              {TASK_LABELS[t] ?? t}
                            </Badge>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <AtSign className="size-4 text-muted-foreground" aria-hidden />
                      {p.instagramUsername ? (
                        <a
                          href={`https://www.instagram.com/${p.instagramUsername}`}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:underline"
                        >
                          @{p.instagramUsername}
                        </a>
                      ) : (
                        <span className="text-muted-foreground">sem Instagram vinculado</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Pixels</CardTitle>
            <CardDescription>
              Pixels (conjuntos de dados) dos Business Managers e contas conectadas.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {pixels.isPending ? (
              <LoadingState rows={3} />
            ) : pixels.isError ? (
              <ErrorState error={pixels.error} onRetry={() => void pixels.refetch()} />
            ) : pixels.data.length === 0 ? (
              <EmptyState
                icon={<Radar className="size-8" aria-hidden />}
                title="Nenhum pixel"
                description="Nenhum pixel foi encontrado nos Business Managers e contas conectados."
                action={irParaIntegracoes}
              />
            ) : (
              <ul className="divide-y">
                {pixels.data.map((px) => (
                  <li key={px.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium">{px.name}</p>
                        {px.isUnavailable ? (
                          <Badge variant="destructive">Indisponível</Badge>
                        ) : px.lastFiredAt ? (
                          <Badge variant="success">Recebendo eventos</Badge>
                        ) : (
                          <Badge variant="secondary">Sem eventos</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        id {px.metaId}
                        {px.ownerBusinessName ? ` · dono: ${px.ownerBusinessName}` : ""}
                        {px.adAccountMetaIds.length > 0
                          ? ` · contas: ${px.adAccountMetaIds.join(", ")}`
                          : " · sem conta vinculada"}
                      </p>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {px.lastFiredAt ? (
                        <span title={formatDate(px.lastFiredAt)}>
                          último evento {formatRelative(px.lastFiredAt)}
                        </span>
                      ) : (
                        "nenhum evento registrado"
                      )}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
